import { RiAlertLine, RiCheckLine, RiCloseLine, RiErrorWarningLine, RiRefreshLine } from "react-icons/ri";
import { PhotoDraft } from "./photoModel";
import { ItemState } from "./useUploadQueue";

export interface PhotoTileProps {
  draft: PhotoDraft;
  fileName?: string;
  selected: boolean;
  conflict: boolean;
  upload?: ItemState;
  onToggleSelect(id: string): void;
  onOpen(id: string): void;
  onRetry?: (id: string) => void;
  onDelete?: (id: string) => void;
}

export default function PhotoTile({ draft, fileName, selected, conflict, upload, onToggleSelect, onOpen, onRetry, onDelete }: PhotoTileProps) {
  const { id, previewUrl, direction, isMain } = draft;

  const borderRing = isMain
    ? "ring-2 ring-amber-400 border-amber-300"
    : selected
    ? "ring-2 ring-blue-500 border-blue-400"
    : conflict
    ? "ring-1 ring-orange-400 border-orange-300"
    : "border-gray-200 hover:border-gray-300";

  return (
    <div
      className={`group relative aspect-square overflow-hidden rounded-lg border bg-gray-50 transition-shadow hover:shadow-md ${borderRing}`}
    >
      {/* Checkbox — larger hit-area via padding trick so it stays visually small but easy to tap */}
      <label
        className="absolute z-10 top-0 left-0 p-1.5 cursor-pointer"
      >
        <input
          type="checkbox"
          aria-label={`เลือก ${fileName ?? id}`}
          checked={selected}
          onChange={() => onToggleSelect(id)}
          className="w-3.5 h-3.5 cursor-pointer accent-blue-600"
        />
      </label>

      <button type="button" data-testid={`tile-body-${id}`} onClick={() => onOpen(id)} className="block w-full h-full">
        <img src={previewUrl} alt={fileName ?? id} className="object-contain w-full h-full p-0.5 transition-transform group-hover:scale-[1.03]" />
      </button>

      {/* Direction badge or unknown-angle warning — hidden on hover to reveal the delete button */}
      {direction ? (
        <span className={`absolute top-1 right-1 rounded bg-blue-600/90 px-1 py-px text-[10px] font-bold text-white leading-none select-none transition-opacity ${onDelete ? "group-hover:opacity-0" : ""}`}>
          {direction}
        </span>
      ) : (
        <span data-testid={`tile-warn-${id}`} className={`absolute top-1 right-1 text-amber-500 drop-shadow-sm transition-opacity ${onDelete ? "group-hover:opacity-0" : ""}`}>
          <RiAlertLine size={14} />
        </span>
      )}

      {/* Per-image delete — appears on hover, top-right */}
      {onDelete && (
        <button
          type="button"
          data-testid={`tile-delete-${id}`}
          aria-label={`ลบ ${fileName ?? id}`}
          onClick={() => onDelete(id)}
          className="absolute top-0.5 right-0.5 z-10 rounded-full bg-red-600 p-0.5 text-white opacity-0 shadow-sm transition-opacity hover:bg-red-700 group-hover:opacity-100"
        >
          <RiCloseLine size={12} />
        </button>
      )}

      {/* Main-image star */}
      {isMain && (
        <span className="absolute bottom-1 right-1 text-amber-400 drop-shadow-sm text-sm leading-none select-none" aria-label="รูปหลัก">
          ★
        </span>
      )}

      {/* Conflict badge — orange, distinct from amber unknown */}
      {conflict && (
        <span className="absolute bottom-1 left-1 rounded bg-orange-500 px-1 py-px text-[9px] font-bold text-white leading-none select-none">
          ซ้ำ
        </span>
      )}

      {/* Upload progress bar */}
      {upload?.status === "uploading" && (
        <span className="absolute inset-x-0 bottom-0 h-1 bg-blue-100">
          <span
            className="block h-full bg-blue-500 transition-[width] duration-300"
            style={{ width: `${upload.progress}%` }}
          />
        </span>
      )}

      {/* Upload done/failed badges */}
      {upload?.status === "success" && (
        <span className="absolute bottom-1 left-1 rounded-full bg-emerald-500 p-0.5 text-white shadow-sm">
          <RiCheckLine size={10} />
        </span>
      )}
      {upload?.status === "failed" && (
        <span className="absolute bottom-1 left-1 rounded-full bg-red-500 p-0.5 text-white shadow-sm">
          <RiErrorWarningLine size={10} />
        </span>
      )}
      {upload?.status === "failed" && onRetry && (
        <button
          type="button"
          data-testid={`tile-retry-${id}`}
          aria-label={`ลองใหม่ ${fileName ?? id}`}
          onClick={() => onRetry(id)}
          className="absolute bottom-1 right-1 rounded-full bg-red-600 p-0.5 text-white shadow-sm hover:bg-red-700 transition-colors"
        >
          <RiRefreshLine size={10} />
        </button>
      )}
    </div>
  );
}
